import React from 'react';
import { Link } from 'react-router-dom';
import type { Associate, Settings } from '../../types';
import { useCollection } from '../../hooks/useCollection';
import OptimizedImage from '../../components/OptimizedImage';
import { useSEO } from '../../hooks/useSEO';

const AssociateCard: React.FC<{ associate: Associate }> = ({ associate }) => {
    return (
        <Link 
            to={`/associates/${associate.slug}`}
            className="group block bg-white p-8 shadow-md hover:shadow-xl transition-shadow duration-300"
        >
            <div className="flex flex-col items-center text-center">
                <OptimizedImage
                    src={associate.logoUrl}
                    size={400}
                    fallbackSrc="https://picsum.photos/400/400"
                    alt={associate.name}
                    className="w-32 h-32 object-contain mb-6 grayscale group-hover:grayscale-0 transition-all duration-300"
                />
                <h3 className="text-2xl font-semibold font-heading text-brand-dark">{associate.name}</h3>
                <p className="text-gray-600 mt-2 text-sm font-body leading-relaxed">{associate.description}</p>
                 <div className="inline-block mt-4 text-sm font-semibold text-brand-dark relative after:content-[''] after:absolute after:left-0 after:bottom-[-2px] after:w-full after:h-[1px] after:bg-brand-dark after:scale-x-0 group-hover:after:scale-x-100 after:origin-left after:transition-transform font-header">
                    View Portfolio
                </div>
            </div>
        </Link>
    );
};


const AssociatesListPage: React.FC<{ settings: Settings }> = ({ settings }) => {
    const { data: associates, isLoading } = useCollection<Associate>('associates');

    useSEO({
        title: `Our Associates | ${settings?.companyName || 'ARCH.'}`,
        description: `Explore the network of associate partners and collaborative practices allied with ${settings?.companyName || 'ARCH.'}.`,
        ogImage: settings?.openGraph?.imageUrl
    }, [settings]);

    return (
        <div className="bg-brand-light text-brand-dark pt-32 pb-16">
            <div className="container mx-auto px-6 max-w-6xl">
                 <div className="text-center max-w-3xl mx-auto mb-16">
                    <h1 className="text-5xl md:text-6xl font-extrabold tracking-tight font-heading">Our Associates</h1>
                    <p className="mt-4 text-lg text-gray-600 font-body">
                        We collaborate with a network of specialized firms to deliver comprehensive and innovative design solutions across various disciplines.
                    </p>
                 </div>

                 {isLoading && <p className="text-center">Loading associates...</p>}
                 
                 {!isLoading && associates && associates.length > 0 && (
                     <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                         {associates.map(associate => (
                            <AssociateCard key={associate.id} associate={associate} />
                         ))}
                     </div>
                 )}

                 {!isLoading && (!associates || associates.length === 0) && (
                     <p className="text-center text-gray-500 py-12">No associates have been added yet.</p>
                 )}
            </div>
        </div>
    );
};

export default AssociatesListPage;
